import {ComboboxItem, ComboboxProvider, SelectProvider} from '@ariakit/react'
import {useMemo, useState} from 'react'
import {DisabilityProfileIcfCode, DisabilityProfileLetter} from '../../types'
import {disabiltyProfileLetterToIcfCodes} from '../../utils'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger
} from '@/components/ui/select-atoms/SelectAtoms'
import {useTranslation} from '@/translations/i18n'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Text} from '@/components/ui/text/Text'

export const SelectIcfCodes = ({
    letter,
    value,
    onChange,
    errorMessage,
    disabled,
    exludedOptions
}: {
    exludedOptions?: DisabilityProfileIcfCode[]
    letter: DisabilityProfileLetter
    value?: DisabilityProfileIcfCode
    onChange: (value: DisabilityProfileIcfCode) => void
    errorMessage?: string
    disabled?: boolean
}) => {
    const {t} = useTranslation()
    const [comboboxValue, setComboboxValue] = useState('')

    const matches = useMemo(() => {
        return disabiltyProfileLetterToIcfCodes(letter).filter(
            icfCode =>
                (icfCode.code.toLowerCase().includes(comboboxValue.toLowerCase()) ||
                    t(icfCode.label).toLowerCase().includes(comboboxValue.toLowerCase())) &&
                !exludedOptions?.includes(icfCode.code)
        )
    }, [comboboxValue])

    return (
        <ComboboxProvider resetValueOnHide setValue={setComboboxValue}>
            <SelectProvider value={value ?? ''} setValue={onChange}>
                <Flexbox direction="column" gap={1.5} fullWidth>
                    <SelectTrigger
                        disabled={disabled}
                        hasError={!!errorMessage}
                        fullWidth
                        style={{whiteSpace: 'nowrap'}}
                        displayValue={value => value}
                        placeholder={t('commons:select')}
                    />
                    <SelectComboboxPopover sameWidth={false}>
                        <SelectComboboxInput />
                        <SelectComboboxList>
                            {matches.map(match => (
                                <SelectItem key={match.code} value={match.code} render={<ComboboxItem />}>
                                    <Flexbox align="center" gap={3}>
                                        {match.code}{' '}
                                        <Text palette="neutral" shade="600">
                                            {t(match.label)}
                                        </Text>
                                    </Flexbox>
                                </SelectItem>
                            ))}
                        </SelectComboboxList>
                    </SelectComboboxPopover>
                    {!!errorMessage && (
                        <Flexbox align="center" gap={errorMessage ? 1.5 : 0}>
                            <InputStatusIcon hasError={!!errorMessage} />
                            <InputHelpText error={errorMessage} />
                        </Flexbox>
                    )}
                </Flexbox>
            </SelectProvider>
        </ComboboxProvider>
    )
}
